// ReportView — renders ANY report definition from src/reports/registry.js.
//
// ⛔ NO REPORT-SPECIFIC LOGIC LIVES HERE. The test of that is report #2: it should require zero
// changes to this file. Everything that varies belongs in the definition; everything that must not
// vary per report (the None option, filtered-not-raw summaries, one export payload) lives in
// ./reports/viewModel so it is testable without a browser and cannot be got wrong per report.
//
// Chip/tile/table styling is lifted from PropertyExecutiveSummary.js unchanged — same visual
// language, deliberately not redesigned.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useYearRange } from '../constants/yearRange';
import ScrollX, { pinCell, PINNED_TABLE, redrawKey } from './ScrollX';
import { formatCell, defaultAlign, rawValue, totalLabel, anyEstimated, anyRecovered, anyEffective, anyNotMeaningful, cellNote, ESTIMATE_FOOTNOTE, RECOVERED_FOOTNOTE, EFFECTIVE_FOOTNOTE, NOT_MEANINGFUL_FOOTNOTE, PROJECTED_FOOTNOTE } from '../reports/format';
import {
  normaliseFilters, initialNonEnumFilterState, applyFilters, pruneSelections, filtersToSaved, filtersFromSaved, sortRows,
  resolveHidden, visibleColumns, exportColumns, computeTotals, buildExportPayload, exportFilename, columnUsed,
} from '../reports/viewModel';
import { exportToCsv } from '../utils/exportCsv';
import { exportSheetsToXlsx } from '../utils/exportXlsx';
import { warnOnDeadFilters } from '../reports/grouping';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import { useUserPref } from '../hooks/useUserPref';
import ReportFilters from './ReportFilters';
import { savePreset, updatePresetOptions } from '../utils/reportPresets';
import { loadClientGroups, resolveClientIds } from '../utils/clientGroups';
import { loadActiveNoticeTypes } from '../utils/noticeTypes';

// ── styling, copied from PropertyExecutiveSummary.js (the filter chips live in ./ReportFilters) ─────────────────
function Tile({ label, value, sub }) {
  return <div style={{ flex: '1 1 180px', border: '1px solid #e6e2da', borderRadius: '12px', padding: '14px 16px', background: '#faf9f7' }}>
    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#8a8578' }}>{label}</div>
    <div style={{ fontSize: '24px', fontWeight: 800, color: '#1c1c1c', marginTop: '4px' }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: '#8a8578', marginTop: '2px' }}>{sub}</div>}
  </div>;
}
const thBase = { padding: '9px 14px', borderBottom: '1px solid #e6e2da', background: '#f4f2ee', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b6658', fontWeight: 700 };
const tdBase = { padding: '8px 14px', borderBottom: '1px solid #f0ede7', fontSize: '13px' };
const tfBase = { padding: '9px 14px', borderTop: '2px solid #d4d0c8', fontSize: '13px', fontWeight: 700, background: '#faf9f7' };
const btn = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '8px', border: '1px solid #d4d0c8', background: '#ffffff', color: '#3c3a33', cursor: 'pointer' };

// ⛔ THE ONLY CELL THAT IS NOT formatCell OUTPUT. formatCell returns a STRING, so a clickable link
// is impossible through it — this is the one narrow escape hatch, added as a shared column TYPE
// rather than a per-definition render hook. A generic hook would let a definition inject arbitrary
// JSX on screen while the exports kept formatting from c.type, so the two surfaces could disagree
// silently; a type cannot, because both exporters see the same declared type and fall through to
// text on purpose.
//
// The URL is shown IN FULL as the link text, ellipsised by CSS rather than by slicing the string:
// a truncated value that reads as complete is the maxLines bug in docs/known-debt.md §3, and here
// the full value stays in the href and the title. Blank renders as the same dash every other
// column uses — an absent source is absent, not an empty link.
// ⛔ THE PROPERTY NAME OPENS THE ROW'S PARCEL CARD (Josh, Philip 2026-10-27) — as an overlay on the report, in the row's
// year (the caller, StaffPortal openReportRow). It reads as plain text: no colour, no underline, only the pointer and a
// faint highlight on hover. A row with no parcel (Portfolio Tax Trend's property rows) stays plain text. On screen only —
// the exports format from the column type.
function PropertyLink({ row, label, onOpen }) {
  const [hover, setHover] = useState(false);
  return (
    <span role="button" tabIndex={0} onClick={() => onOpen(row)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(row); } }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title={`Open ${row.account_number || 'this parcel'} (${row.tax_year})`}
      style={{ cursor: 'pointer', borderRadius: '4px', padding: '1px 3px', margin: '-1px -3px',
        background: hover ? 'rgba(60,58,51,0.08)' : 'transparent', transition: 'background 0.12s' }}>
      {label}
    </span>
  );
}

function LinkCell({ value }) {
  const url = value == null || value === '' ? null : String(value);
  if (!url) return '\u2014';
  const external = /^https?:\/\//i.test(url);
  if (!external) return url;          // not a URL we should turn into a target
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={url}
      style={{ display: 'inline-block', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', verticalAlign: 'bottom', color: '#2f5fa8', textDecoration: 'underline' }}>
      {url}
    </a>
  );
}

// Which years a report opens with. Pure — the sessionStorage read happens at the call site.
//
// ⛔ AN OUT-OF-RANGE STORED YEAR FALLS BACK TO THE LATEST CONFIGURED ONE, NOT TO NOTHING. wh_valueYear is
// app-wide and folder_year_config can move underneath it, so a year the user last looked at may not be
// selectable here. Selecting nothing would render an empty report indistinguishable from a broken one.
export function initialYearSelection(configYears, storedYear, yearsDefault = 'current') {
  const list = (configYears || []).map(String);
  if (!list.length) return [];
  if (yearsDefault === 'all') return list;
  const stored = storedYear == null ? null : String(storedYear);
  return [stored && list.includes(stored) ? stored : list[list.length - 1]];
}

// The Clients pick (step 8): clients and client groups. Opens with none picked (a saved report: its own) — the report
// waits for a pick — and "+ client or group" adds more; every picked one has its × (Philip 2026-10-23). A group stands
// for its clients at run time (utils/clientGroups). With several clients, the report gains a Client column.
const CLIENT_COLUMN = { key: 'client', label: 'Client', type: 'text' };
// Shown when the rows carry a notice name — the "Notices: All notices" option (step 8): Original, Supplemental, …
const NOTICE_COLUMN = { key: 'notice', label: 'Notice', type: 'text' };
const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '13px', borderRadius: '8px', border: '1px solid #d4d0c8' };
const xStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#8a8578', padding: 0, fontSize: '13px', lineHeight: 1 };
function ClientPick({ allClients, groups, clientIds, groupIds, onChange }) {
  const nameOf = (id) => allClients.find((c) => c.id === id)?.name || id;
  const groupName = (id) => groups.find((g) => g.id === id)?.name || 'Group';
  const restClients = allClients.filter((c) => !clientIds.includes(c.id));
  const restGroups = groups.filter((g) => g.active && !groupIds.includes(g.id));
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
      {groupIds.map((id) => (
        <span key={`g:${id}`} style={chipStyle} title="Client group">
          ▦ {groupName(id)}
          <button type="button" title={`Remove ${groupName(id)}`} onClick={() => onChange(clientIds, groupIds.filter((x) => x !== id))} style={xStyle}>×</button>
        </span>
      ))}
      {clientIds.map((id) => (
        <span key={id} style={chipStyle}>
          {nameOf(id)}
          <button type="button" title={`Remove ${nameOf(id)}`} onClick={() => onChange(clientIds.filter((x) => x !== id), groupIds)} style={xStyle}>×</button>
        </span>
      ))}
      {(restClients.length > 0 || restGroups.length > 0) && (
        <select value="" onChange={(e) => {
          const v = e.target.value;
          if (v.startsWith('g:')) onChange(clientIds, [...groupIds, v.slice(2)]);
          else if (v) onChange([...clientIds, v], groupIds);
        }} style={{ padding: '5px 8px', fontSize: '13px', borderRadius: '8px', border: '1px solid #d4d0c8' }}>
          <option value="">{groups.length ? '+ client or group' : '+ client'}</option>
          {restGroups.length > 0 && <optgroup label="Groups">{restGroups.map((g) => <option key={g.id} value={`g:${g.id}`}>{g.name}</option>)}</optgroup>}
          {restClients.length > 0 && <optgroup label="Clients">{restClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
        </select>
      )}
    </div>
  );
}

// `preset`: a saved report (report_presets, step 8) — the report opens from its options instead of the defaults.
export default function ReportView({ definition, allClients = [], onBack, ownerId = null, preset = null, onOpenParcel = null }) {
  const { years: configYears } = useYearRange();          // ⛔ folder_year_config, never a hardcoded 2026

  const filters = useMemo(() => normaliseFilters(definition), [definition]);
  const wantsClient = filters.some((f) => f.type === 'client');
  const wantsYears = filters.some((f) => f.type === 'years');

  // ── SQL-side filter state: clients + years are the only two pushed into fetch() ──────────────
  const [groupIds, setGroupIds] = useState(() => preset?.options?.groupIds || []);
  const [clientIds, setClientIds] = useState(() => {
    // A saved report opens with its clients; otherwise nothing is picked (Philip 2026-10-23: a refresh must not pick a
    // client nobody chose — it used to take the first client in the list, which a newly added client could become).
    return (preset?.options?.clientIds || []).filter((id) => allClients.some((c) => c.id === id));
  });
  // The Notices chips' types (a report with a 'notices' filter only).
  const wantsNotices = filters.some((f) => f.type === 'notices');
  const [noticeTypes, setNoticeTypes] = useState([]);
  useEffect(() => {
    if (!wantsNotices) return undefined;
    let alive = true;
    loadActiveNoticeTypes().then((r) => { if (alive) { setNoticeTypes(r.ok ? r.data : []); if (!r.ok) setGroupsErr(r.error); } });
    return () => { alive = false; };
  }, [wantsNotices]);
  const [groups, setGroups] = useState([]);
  const [groupsErr, setGroupsErr] = useState('');
  useEffect(() => {
    if (!wantsClient) return undefined;
    let alive = true;
    loadClientGroups().then((r) => { if (alive) { setGroups(r.ok ? r.data : []); setGroupsErr(r.ok ? '' : r.error); } });
    return () => { alive = false; };
  }, [wantsClient]);
  // What the report runs for: the clients picked one by one plus each picked group's clients.
  const runIds = resolveClientIds(clientIds, groupIds, groups);
  const clientKey = runIds.join(',');
  const multiClient = runIds.length > 1;
  const clientName = [...groupIds.map((id) => groups.find((g) => g.id === id)?.name || ''),
    ...clientIds.map((id) => allClients.find((c) => c.id === id)?.name || '')].filter(Boolean).join(', ');
  const setPick = (cids, gids) => { setClientIds(cids); setGroupIds(gids); };
  const [allYears, setAllYears] = useState(!!preset?.options?.allYears);

  // ═══ WHICH YEARS ARE SELECTED WHEN A REPORT OPENS ═══
  //
  // ⛔ EVERY YEAR USED TO BE PRE-SELECTED, so reaching a useful view meant DESELECTING seven. With four
  // parcels that is mild; across Hertz's 21 states it opens thousands of rows nobody asked for. The current
  // value year is what someone wants most of the time, and "All years" is already one click (the bubble
  // below), so the comparison case costs one click instead of seven.
  //
  // ⛔ NOT BLANK. An empty report that needs a click before showing anything is a different annoyance, not a
  // smaller one.
  //
  // ⛔ AND NOT UNIFORMLY ONE YEAR. Portfolio Tax Trend exists to compare years — its exportSheets computes
  // year-over-year deltas against the previous year — so a single-year default would make it a trend of one
  // point: broken, not tidier. A definition says so with `yearsDefault: 'all'` rather than this component
  // knowing report names.
  const yearsDefault = definition.yearsDefault || 'current';
  const initialYears = () => {
    // ⛔ THE SAME sessionStorage KEY setValueYear WRITES — one value year across the app, not a report-local
    // idea of "now". Read here rather than inside the helper so the decision itself stays pure and testable.
    let stored = null;
    try { stored = sessionStorage.getItem('wh_valueYear'); } catch (e) { stored = null; }
    return initialYearSelection(configYears, stored, yearsDefault);
  };
  const [years, setYears] = useState(() => (preset?.options?.years?.length ? preset.options.years.map(String) : initialYears()));
  // ⛔ SEEDS ONCE, NEVER CLOBBERS. This was `setYears(configYears)` on every change of configYears — which
  // arrives asynchronously from folder_year_config, so it fired AFTER first paint and would now overwrite a
  // selection the user had already made. It only fills in when the initial render had no years to choose from.
  const yearsSeeded = useRef(false);
  useEffect(() => {
    if (yearsSeeded.current || !configYears.length) return;
    yearsSeeded.current = true;
    setYears((ys) => (ys.length ? ys : initialYears()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configYears]);

  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // ── client-side filter state ─────────────────────────────────────────────────────────────────
  // Toggles/searches are declared defaults and exist from mount, because buildRows reads them. Enum selections
  // start EMPTY, which means all (viewModel — nothing selected = all), so nothing waits on the rows to seed them.
  const [fstate, setFstate] = useState(() => initialNonEnumFilterState(definition));

  // ⛔ fetch() SEES the current filters but does NOT re-run when they change. A chip click must not
  // refetch — that is the whole reason client and years are the only SQL-side inputs. A filter that
  // genuinely needs a new query would need its own dependency here; deliberately not built until
  // one exists, rather than guessing at the mechanism.
  const filtersRef = useRef(fstate);
  filtersRef.current = fstate;

  // ⛔ RESET ON CLIENT SWITCH TOO, not only on report switch. Enum options are DERIVED FROM THE
  // ROWS, so they are client-specific: a property chip lit for LaSalle's "18 East" survives into
  // the next client, matches nothing there, and filters the whole report to empty — a blank screen
  // mid-demo with no obvious cause. The defaults are re-applied, then that client's remembered
  // selection (below) is laid over them.
  // A saved report's filters are laid down once, on the first run, in place of the defaults.
  const presetFilters = useRef(preset?.options?.filters || null);
  useEffect(() => {
    setFstate(presetFilters.current ? filtersFromSaved(definition, presetFilters.current) : initialNonEnumFilterState(definition));
    presetFilters.current = null;
  }, [definition?.id, definition, clientKey]);

  // ═══ REMEMBERED PER USER, PER REPORT, PER CLIENT (Philip, 2026-10-11) ═══
  // user_column_prefs — the Columns menu's table — under 'report-filters:<report>:<client>' (no client part on a
  // report without a client picker). Per client because the choices are: DTAG's states and properties mean nothing on
  // LaSalle. Applied ONCE per key when it loads — re-applying after each save would wipe a search being typed.
  // One client: the same key as before step 8. Several, or groups: one key per pick.
  const filterPrefKey = definition?.id && (!wantsClient || clientIds.length || groupIds.length)
    ? `report-filters:${definition.id}${wantsClient ? `:${[...[...clientIds].sort(), ...[...groupIds].sort().map((g) => `g:${g}`)].join('+')}` : ''}` : null;
  const { saved: savedFilters, save: saveFilters } = useUserPref(filterPrefKey, ownerId);
  // A saved report brings its own filters: this person's remembered ones are not laid over them on opening.
  const appliedPrefKey = useRef(preset ? filterPrefKey : null);
  useEffect(() => {
    if (savedFilters == null || appliedPrefKey.current === filterPrefKey) return;
    appliedPrefKey.current = filterPrefKey;
    if (savedFilters.length) setFstate(filtersFromSaved(definition, savedFilters));
  }, [savedFilters, filterPrefKey, definition]);
  const [filterSaveErr, setFilterSaveErr] = useState('');
  const setFilters = (next, remember) => {
    setFstate(next);
    if (!remember || !ownerId || !filterPrefKey) return;
    saveFilters(filtersToSaved(definition, next)).then(() => setFilterSaveErr(''), (e) => {
      console.error('[ReportView] filter choice not saved:', e);
      setFilterSaveErr('Your filters are applied but were not saved — they will reset next time.');
    });
  };
  // ── fetch. Deps are the SQL-side inputs only: a client-side filter must never refetch ────────
  const yearsKey = allYears ? 'all' : years.join(',');
  useEffect(() => {
    if (!definition?.fetch) { setRaw([]); return undefined; }
    if (wantsClient && !runIds.length) { setRaw([]); return undefined; }
    let cancelled = false;
    setLoading(true); setErr(null);
    Promise.resolve(definition.fetch({ clientIds: runIds, years: allYears ? null : years, allYears, filters: filtersRef.current }))
      .then((rows) => { if (!cancelled) setRaw(rows || []); })
      .catch((e) => { if (!cancelled) { setErr(e?.message || String(e)); setRaw([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [definition?.id, clientKey, yearsKey]);

  const built = useMemo(() => {
    if (!definition) return [];
    try {
      const out = definition.buildRows ? definition.buildRows(raw, { clientIds: runIds, years, allYears, filters: fstate }) : raw;
      // Several clients: each row names its client (the Client column).
      return multiClient ? out.map((r) => ({ ...r, client: allClients.find((c) => c.id === r.client_id)?.name || '' })) : out;
    }
    catch (e) { console.error('[ReportView] buildRows threw:', e); return []; }
  }, [definition, raw, clientKey, years, allYears, fstate, allClients]);

  // A filter pointing at a field no row carries matches everything and looks like it works. Same class as the
  // group-key bug; a warning is enough because an over-broad filter is not a wrong number. Once per report + client.
  // ⛔ Must sit BELOW `built`: a dep array evaluates during render (TDZ).
  const deadChecked = useRef(null);
  useEffect(() => {
    const k = `${definition?.id}|${clientKey}`;
    if (!built.length || deadChecked.current === k) return;
    deadChecked.current = k;
    warnOnDeadFilters(built, normaliseFilters(definition), definition.id);
  }, [built, definition, clientKey]);

  // ═══ COLUMNS, REMEMBERED PER USER PER REPORT (Philip, 2026-10-10) ═══
  // user_column_prefs row (owner = the staff id, table_key 'report:<id>') holding ONLY the columns this user has
  // switched, as [{ key, visible }]; everything else follows the report's defaults, so a column added to a report
  // later still arrives with its own default. The same selection drives the screen, CSV, XLSX and PDF.
  // `touched` = the switched columns: an exportDefault column rides into the workbook only while untouched.
  const { saved: savedCols, save: saveCols } = useColumnPrefs(definition?.id ? `report:${definition.id}` : null, [], ownerId);
  const [hidden, setHidden] = useState(() => resolveHidden(definition, null));
  const [touched, setTouched] = useState(() => new Set());
  const [colSaveErr, setColSaveErr] = useState('');
  useEffect(() => {
    setHidden(resolveHidden(definition, savedCols));
    setTouched(new Set((savedCols || []).map((e) => e?.key).filter(Boolean)));
    setColSaveErr('');
  }, [definition, savedCols]);
  const toggleColumn = (key) => {
    const nextHidden = hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
    const nextTouched = new Set(touched).add(key);
    setHidden(nextHidden); setTouched(nextTouched);
    if (!ownerId) return;
    saveCols([...nextTouched].map((k) => ({ key: k, visible: !nextHidden.includes(k) })))
      .then(() => setColSaveErr(''), (e) => {
        console.error('[ReportView] column choice not saved:', e);
        setColSaveErr('Your column choice is applied but was not saved — it will reset next time.');
      });
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  // The Columns menu closes on a click outside it or Esc, as well as on Done.
  const pickerRef = useRef(null);
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [pickerOpen]);
  const [sort, setSort] = useState(null);
  useEffect(() => { setSort(null); }, [definition?.id]);

  const noticeRows = built.some((r) => r.notice);
  // A report with its own Client column (Tax Rates) keeps it, rather than getting a second one.
  const withClientColumn = (cs) => [...(multiClient && !cs.some((c) => c.key === 'client') ? [CLIENT_COLUMN] : []), ...(noticeRows ? [NOTICE_COLUMN] : []), ...cs];
  // A showWhenUsed column (the Note column) appears only when some row has a value in it — on screen as in the exports.
  const cols = useMemo(() => withClientColumn(visibleColumns(definition, hidden))
    .filter(columnUsed(built)), [definition, hidden, multiClient, noticeRows, built]);
  // ⛔ CLIENT AND PROPERTY ARE PINNED ON THE LEFT OF THE SCREEN TABLE, AND SO SHOWN FIRST (Philip 2026-10-27), at fixed
  // widths (sticky cells need known offsets), the property name wrapping instead of squeezing. The exports keep `cols`.
  const PIN_W = { client: 160, property: 220 };
  const screenCols = [...Object.keys(PIN_W).map((k) => cols.find((c) => c.key === k)).filter(Boolean),
    ...cols.filter((c) => !(c.key in PIN_W))];
  const pinnedCols = screenCols.filter((c) => c.key in PIN_W);
  const pinAt = (c, background, header = false) => {
    const i = pinnedCols.indexOf(c);
    if (i < 0) return {};
    const w = PIN_W[c.key];
    const left = pinnedCols.slice(0, i).reduce((sum, p) => sum + PIN_W[p.key], 0);
    return { width: `${w}px`, minWidth: `${w}px`, maxWidth: `${w}px`, whiteSpace: 'normal', wordBreak: 'break-word',
      ...pinCell(left, { background, header, last: i === pinnedCols.length - 1 }) };
  };

  // ⛔ ONE row set feeds the table, the summary, the totals AND both exports. If a summary said
  // "48 pending" while the table below listed 31 because a filter was lit, the report would be
  // wrong in front of a client — so nothing downstream is allowed to see the unfiltered rows.
  const rows = useMemo(() => sortRows(applyFilters(built, definition, fstate), definition, sort), [built, definition, fstate, sort]);

  const summary = useMemo(() => {
    if (!definition?.summary) return null;
    try { return definition.summary(rows, { clientIds: runIds, years, allYears, filters: fstate }) || null; }
    catch (e) { console.error('[ReportView] summary threw:', e); return null; }
  }, [definition, rows, clientKey, years, allYears, fstate]);

  const totals = useMemo(() => computeTotals(rows, cols), [rows, cols]);
  const bodyKey = useMemo(() => redrawKey(rows.map((r, i) => r.__key || i)), [rows]);   // a fresh body per row set (ScrollX redrawKey)
  const showFootnote = useMemo(() => anyEstimated(rows, cols), [rows, cols]);
  const showRecovered = useMemo(() => anyRecovered(rows, cols), [rows, cols]);
  const showEffective = useMemo(() => anyEffective(rows, cols), [rows, cols]);
  const showNotMeaningful = useMemo(() => anyNotMeaningful(rows, cols), [rows, cols]);

  // ── export: one payload, both formats ────────────────────────────────────────────────────────
  const doExport = useCallback((kind) => {
    // ⛔ The workbook may carry columns the screen hides (exportDefault); the CSV and the sheets
    // both come from ONE payload, so they cannot disagree about which columns those are.
    const xCols = withClientColumn(exportColumns(definition, hidden, touched));
    const payload = buildExportPayload({
      definition, rows, columns: xCols, totals: computeTotals(rows, xCols), summary,
      ctx: { clientIds: runIds, clientName, years, allYears, filters: fstate },
    });
    if (kind === 'csv') {
      exportToCsv(exportFilename(definition, 'csv'), payload.columns, [
        ...payload.rows,
        ...(payload.totals ? [{ ...payload.totals, [payload.columns[0].key]: totalLabel(payload.totals) }] : []),
        ...payload.footnotes.map((n) => ({ [payload.columns[0].key]: n })),
      ]);
    } else {
      // One workbook, however many sheets the payload built. CSV stays detail-only — it has no sheets.
      exportSheetsToXlsx(exportFilename(definition, 'xlsx'), payload.sheets);
    }
  }, [definition, rows, cols, hidden, touched, totals, summary, clientKey, clientName, multiClient, noticeRows, years, allYears, fstate]);

  // ── PDF ────────────────────────────────────────────────────────────────────────────────────
  // ⛔ ONE YEAR PER DOCUMENT. When more than one year is on screen the button is DISABLED with a
  // tooltip rather than silently emitting N files: a browser will block or drop most of a burst of
  // downloads, so "one click, three PDFs" fails quietly and unevenly. Disabling makes the rule
  // visible, the same honesty as the CSV button saying "(detail only)".
  const pdfYears = useMemo(() => [...new Set(rows.map((r) => String(r.tax_year)).filter(Boolean))], [rows]);
  const pdfReady = !!definition.pdf && pdfYears.length === 1 && rows.length > 0;
  const [pdfBusy, setPdfBusy] = useState(false);
  const doExportPdf = useCallback(async () => {
    if (!pdfReady) return;
    setPdfBusy(true);
    try {
      // Dynamic import: @react-pdf is large and must not ride in the main bundle, and a load
      // failure here surfaces as a message rather than an opaque crash. Same reason
      // api/generate-tbt.js does it.
      const P = await import('@react-pdf/renderer');
      const { buildDocument } = await import('../reports/pdf/executiveSummaryPdf');
      const xCols = withClientColumn(exportColumns(definition, hidden, touched));
      const doc = buildDocument(P, {
        rows, year: pdfYears[0], columns: xCols,
        clientName,
        totals: computeTotals(rows, xCols),
      });
      const blob = await P.pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename(definition, 'pdf').replace('.pdf', `-${pdfYears[0]}.pdf`);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[ReportView] PDF export failed:', e);
      setErr(`PDF export failed: ${e?.message || e}`);
    } finally {
      setPdfBusy(false);
    }
  }, [pdfReady, definition, rows, pdfYears, hidden, touched, clientName, multiClient, noticeRows]);

  // ── saved reports (report_presets) ───────────────────────────────────────────────────────────
  const [presetMsg, setPresetMsg] = useState(null);   // { ok, text }
  const presetOptions = () => ({ clientIds, groupIds, years, allYears, filters: filtersToSaved(definition, fstate) });
  const saveAsPreset = async () => {
    const name = window.prompt('Name this saved report');
    if (!name || !name.trim()) return;
    const r = await savePreset({ ownerId, name, reportId: definition.id, options: presetOptions() });
    setPresetMsg(r.ok ? { ok: true, text: `Saved as "${r.data.name}" — it's on Live Reporting.` } : { ok: false, text: r.error });
  };
  const updateThisPreset = async () => {
    if (!window.confirm(`Overwrite "${preset.name}" with the current clients, years and filters?`)) return;
    const r = await updatePresetOptions(preset.id, presetOptions());
    setPresetMsg(r.ok ? { ok: true, text: `"${preset.name}" updated.` } : { ok: false, text: r.error });
  };

  // Defensive only — StaffPortal now redirects before it can mount this without a definition.
  // ⛔ Still not a bare dead end: no screen in the app should be a terminal string with no way out.
  if (!definition) {
    return (
      <div style={{ padding: '20px 24px', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ fontSize: '13px', color: '#8a8578', marginBottom: '10px' }}>That report is no longer available.</div>
        {onBack && <button type="button" style={btn} onClick={onBack}>← Back to Live Reporting</button>}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 24px', fontFamily: 'system-ui, sans-serif', maxWidth: '1240px', margin: '0 auto' }}>
      {onBack && <button style={{ ...btn, marginBottom: '12px' }} onClick={onBack}>← Back</button>}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>{definition.title}</h1>
        {wantsClient && <ClientPick allClients={allClients} groups={groups} clientIds={clientIds} groupIds={groupIds} onChange={setPick} />}
      </div>
      {definition.description && <div style={{ fontSize: '13px', color: '#8a8578', marginTop: '4px' }}>{definition.description}</div>}
      {groupsErr && <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>{groupsErr}</div>}

      {/* ── FILTER BAR, entirely from definition.filters (./ReportFilters — the one used by every report) ── */}
      <ReportFilters filters={filters} definition={definition} rows={built} state={fstate} noticeTypes={noticeTypes}
        onEnum={(key, values) => setFilters(pruneSelections(built, definition, { ...fstate, [key]: values }, key), true)}
        onToggle={(key, value) => setFilters(definition.onToggle ? definition.onToggle(key, value, fstate) : { ...fstate, [key]: value }, true)}
        onNote={(key, value) => setFilters({ ...fstate, [key]: value }, true)}
        onSearch={(key, text) => setFilters({ ...fstate, [key]: text }, false)}
        years={{ show: wantsYears, configYears, years, allYears, setYears, setAllYears }} />
      <div style={{ marginTop: '10px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div ref={pickerRef} style={{ position: 'relative' }}>
            <button type="button" style={btn} onClick={() => setPickerOpen((o) => !o)}>⊞ Columns ▾</button>
            {pickerOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, background: '#fff', border: '1px solid #e6e2da', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '8px 0', minWidth: '220px' }}>
                {(definition.columns || []).map((c) => {
                  const on = !hidden.includes(c.key);
                  const locked = !!c.locked;   // the columns that identify a row (Year, Account #)
                  return (
                    <label key={c.key} title={locked ? 'Always shown — it identifies the row' : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 14px', fontSize: '13px', cursor: locked ? 'not-allowed' : 'pointer', color: locked ? '#8a8578' : '#3c3a33' }}>
                      <input type="checkbox" checked={on} disabled={locked}
                        onChange={() => toggleColumn(c.key)} />
                      <span style={{ flex: 1 }}>{c.label}</span>
                      {locked && <span style={{ fontSize: '10px', letterSpacing: '0.04em', color: '#a9a396' }}>ALWAYS</span>}
                    </label>
                  );
                })}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 14px 2px', borderTop: '1px solid #eee', marginTop: '4px' }}>
                  <button type="button" style={btn} onClick={() => setPickerOpen(false)}>Done</button>
                </div>
              </div>
            )}
          </div>
          <button type="button" style={btn} disabled={!rows.length} onClick={() => doExport('csv')}
            title={definition.exportSheets ? 'Detail rows only — CSV has no sheets. Use XLSX for the summary.' : undefined}>
            Export CSV{definition.exportSheets ? ' (detail only)' : ''}
          </button>
          <button type="button" style={btn} disabled={!rows.length} onClick={() => doExport('xlsx')}
            title={definition.exportSheets ? 'Multi-sheet workbook — see the tabs.' : undefined}>
            Export XLSX{definition.exportSheets ? ' (multi-sheet)' : ''}
          </button>
          {definition.pdf && (
            <button type="button" style={{ ...btn, opacity: pdfReady && !pdfBusy ? 1 : 0.5, cursor: pdfReady && !pdfBusy ? 'pointer' : 'not-allowed' }}
              disabled={!pdfReady || pdfBusy} onClick={doExportPdf}
              title={!rows.length ? 'Nothing to export'
                : pdfYears.length > 1 ? `One year per PDF — ${pdfYears.length} years are on screen. Narrow to a single year.`
                : 'Client-facing PDF for this year'}>
              {pdfBusy ? 'Building PDF…' : 'Export PDF'}
            </button>
          )}
          {/* Opened from a saved report: Save (overwrite it, after asking) or Save as new. Otherwise: Save as my report. */}
          {ownerId && preset && (
            <button type="button" style={btn} onClick={updateThisPreset} title={`Store the current clients, years and filters in "${preset.name}"`}>★ Save</button>
          )}
          {ownerId && (
            <button type="button" style={btn} onClick={saveAsPreset} title="Keep these clients, years and filters as a new card on Live Reporting">
              {preset ? '★ Save as new…' : '★ Save as my report…'}
            </button>
          )}
          {presetMsg && <span style={{ fontSize: '12px', color: presetMsg.ok ? '#15803d' : '#b45309' }}>{presetMsg.text}</span>}
          <span style={{ fontSize: '12px', color: '#8a8578' }}>{rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'}</span>
          {colSaveErr && <span style={{ fontSize: '12px', color: '#b45309' }}>{colSaveErr}</span>}
          {filterSaveErr && <span style={{ fontSize: '12px', color: '#b45309' }}>{filterSaveErr}</span>}
        </div>
      </div>

      {/* ── SUMMARY — computed from the SAME filtered rows the table shows ─────────────────── */}
      {summary?.length > 0 && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '18px' }}>
          {summary.map((s, i) => <Tile key={i} label={s.label} value={s.value} sub={s.sub} />)}
        </div>
      )}

      {/* ── TABLE ──────────────────────────────────────────────────────────────────────────── */}
      <div style={{ marginTop: '18px', border: '1px solid #e6e2da', borderRadius: '12px', overflow: 'hidden' }}>
        {err ? (
          <div style={{ padding: '24px', color: 'var(--red)', fontSize: '13px' }}>
            <strong>Report failed to load.</strong><div style={{ marginTop: '6px', color: '#8a8578' }}>{err}</div>
          </div>
        ) : wantsClient && !runIds.length ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#8a8578', fontSize: '13px' }}>Pick a client or group to run this report.</div>
        ) : loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#8a8578', fontSize: '13px' }}>Loading…</div>
        ) : !rows.length ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#8a8578', fontSize: '13px' }}>
            {built.length ? 'No rows match the current filters.'
              : (definition.emptyMessage || 'No data for this selection.')}
          </div>
        ) : (
          <ScrollX>
            <table style={{ width: '100%', ...PINNED_TABLE }}>
              <thead><tr>
                {screenCols.map((c) => (
                  <th key={c.key} onClick={() => setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === 'asc' ? 'desc' : 'asc' }))}
                    style={{ ...thBase, textAlign: c.align || defaultAlign(c.type), cursor: 'pointer', whiteSpace: 'nowrap', ...pinAt(c, thBase.background, true) }}>
                    {c.label}{sort?.key === c.key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                  </th>
                ))}
              </tr></thead>
              <tbody key={bodyKey}>
                {rows.map((r, i) => (
                  /* ⛔ A BASELINE ROW MUST NOT READ AS WORK WE PERFORMED. It carries the client's own figures
                     from before we were engaged, and this report is the document that claims savings — so the
                     row is muted and the FIRST cell carries the word, not a symbol. Reduced contrast follows
                     how a retired parcel is already dimmed on the Properties grid, so it reads as a known
                     state rather than a rendering fault. The footnote (viewModel) says what it means; the
                     savings column is null by construction (parcelYearRows), so the total cannot include it
                     whatever anyone reads into the styling. */
                  <tr key={r.__key || i} style={r.baseline ? { opacity: 0.62 } : undefined}>
                    {screenCols.map((c, ci) => (
                      <td key={c.key} title={cellNote(r[c.key]) || undefined}
                        style={{ ...tdBase, textAlign: c.align || defaultAlign(c.type), ...(cellNote(r[c.key]) ? { cursor: 'help', textDecoration: 'underline dotted' } : {}), ...pinAt(c, '#ffffff') }}>
                        {r.baseline && ci === 0 && (
                          <span style={{ display: 'inline-block', marginRight: '6px', padding: '1px 6px', borderRadius: '10px',
                            fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap',
                            background: 'rgba(120,128,160,0.18)', color: 'var(--slate)' }}>BASELINE</span>
                        )}
                        {c.type === 'link' ? <LinkCell value={rawValue(r[c.key])} />
                          : c.key === 'property' && onOpenParcel && r.parcel_id
                            ? <PropertyLink row={r} label={formatCell(r[c.key], c.type)} onOpen={onOpenParcel} />
                            : formatCell(r[c.key], c.type)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot><tr>
                  {screenCols.map((c, i) => (
                    <td key={c.key} style={{ ...tfBase, textAlign: c.align || defaultAlign(c.type), ...pinAt(c, tfBase.background) }}>
                      {i === 0 ? totalLabel(totals) : (c.total ? formatCell(totals[c.key], c.type) : '')}
                    </td>
                  ))}
                </tr></tfoot>
              )}
            </table>
          </ScrollX>
        )}
      </div>

      {/* ⛔ Only when a VISIBLE row actually carries a flag — a footnote about a marker that
          appears nowhere teaches readers to ignore it when it does matter. */}
      {showFootnote && <div style={{ marginTop: '8px', fontSize: '11px', color: '#8a8578' }}>{ESTIMATE_FOOTNOTE}</div>}
      {showRecovered && <div style={{ marginTop: showFootnote ? '2px' : '8px', fontSize: '11px', color: '#8a8578' }}>{RECOVERED_FOOTNOTE}</div>}
      {showEffective && <div style={{ marginTop: showFootnote || showRecovered ? '2px' : '8px', fontSize: '11px', color: '#8a8578' }}>{EFFECTIVE_FOOTNOTE}</div>}
      {rows.some((r) => r.projected) && cols.some((c) => c.key === 'savings') && <div style={{ marginTop: '2px', fontSize: '11px', color: '#8a8578' }}>{PROJECTED_FOOTNOTE}</div>}
      {showNotMeaningful && <div style={{ marginTop: showFootnote || showRecovered || showEffective ? '2px' : '8px', fontSize: '11px', color: '#8a8578' }}>{NOT_MEANINGFUL_FOOTNOTE}</div>}
    </div>
  );
}
